import { z } from "zod";

import { simpleDateSchema } from "@/src/models/common-schema";
import { ImagingResultsDocumentType } from "@/src/models/constants/document-types";
import { ImagingType } from "@/src/models/constants/imaging-types";
import {
  ORDERING_PROVIDER_PROMPT,
  BODY_PART_PROMPT,
  BODY_PARTS_PROMPT,
  IMAGING_COMPANY_NAME_PROMPT,
  BODY_PART_LATERALITY_PROMPT,
  ABNORMAL_RESULT_PROMPT,
  FOLLOW_UP_REQUIRED_PROMPT,
  IMPRESSION_PROMPT,
  STUDY_TYPE_PROMPT,
} from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { locationSchema } from "@/src/models/location";
import { Patient, patientMinimalSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const imagingResultsTypeDisplay: Record<ImagingResultsDocumentType, string> = {
  [ImagingResultsDocumentType.BARIUM_SWALLOW]: "Modified Barium Swallow",
  [ImagingResultsDocumentType.CLINICAL]: "Clinical Imaging",
  [ImagingResultsDocumentType.COLONOSCOPY]: "Colonoscopy",
  [ImagingResultsDocumentType.CT]: "CT",
  [ImagingResultsDocumentType.DEXA]: "DEXA",
  [ImagingResultsDocumentType.ECHO]: "Echo",
  [ImagingResultsDocumentType.EEG]: "EEG",
  [ImagingResultsDocumentType.EKG]: "EKG",
  [ImagingResultsDocumentType.EMG]: "EMG",
  [ImagingResultsDocumentType.ENG]: "ENG/VNG",
  [ImagingResultsDocumentType.ESOPHAGRAM]: "Esophagram",
  [ImagingResultsDocumentType.HOLTER_MONITOR]: "Holter Monitor",
  [ImagingResultsDocumentType.MAMMOGRAM]: "Mammogram",
  [ImagingResultsDocumentType.MULTI]: "Multiple Imaging Results",
  [ImagingResultsDocumentType.MRI]: "MRI",
  [ImagingResultsDocumentType.NM]: "Nuclear",
  [ImagingResultsDocumentType.OTHER]: "Imaging Results - Other",
  [ImagingResultsDocumentType.OTHER_CARDIAC]: "Other Cardiac",
  [ImagingResultsDocumentType.PET]: "PET",
  [ImagingResultsDocumentType.PULMONARY_FUNCTION]: "Pulmonary Function/Spiro",
  [ImagingResultsDocumentType.RETINAL]: "Retinal",
  [ImagingResultsDocumentType.STRESS_TEST]: "Stress Test",
  [ImagingResultsDocumentType.STRESS_TEST_EXERCISE]: "Stress Test (exercise)",
  [ImagingResultsDocumentType.STRESS_TEST_NM]: "Stress Test (nm)",
  [ImagingResultsDocumentType.ULTRASOUND]: "Ultrasound",
  [ImagingResultsDocumentType.X_RAY]: "X-Ray",
  [ImagingResultsDocumentType.NEEDS_SIGNATURE]: "Imaging Orders - Signature Needed",
  [ImagingResultsDocumentType.NEEDS_CLARIFICATION]: "Imaging Orders - Clarification Needed",
  [ImagingResultsDocumentType.IMAGING_RESULTS]: "Imaging Results",
  [ImagingResultsDocumentType.ENDOSCOPY]: "Endoscopy",
  [ImagingResultsDocumentType.MONITOR_NOTIFICATION]: "Monitor Notification",
  [ImagingResultsDocumentType.CARDIAC_LEG_IMAGING]: "Cardiac Leg Imaging",
};

export const imagingResultsTypeSchema = z.nativeEnum(ImagingResultsDocumentType);

const baseImagingResultsFields = {
  patient: patientMinimalSchema,
  dateOfImagingResults: simpleDateSchema
    .nullable()
    .describe(
      "Date the imaging results were collected. Look for content in the document like: " +
        "'Exam Date', 'Report Date', ..." +
        "There should be a clear indication of what the date represents from the context of the document. " +
        "Format as YYYY-MM-DD."
    ),
  receivingLocation: locationSchema.nullable(),
  receivingProvider: practitionerSchema.nullable(),
  abnormal: z.boolean().nullable().describe(ABNORMAL_RESULT_PROMPT),
  followUpRequired: z.boolean().nullable().describe(FOLLOW_UP_REQUIRED_PROMPT),
  impression: z.string().nullable().describe(IMPRESSION_PROMPT),
  studyType: z.string().nullable().describe(STUDY_TYPE_PROMPT),
};
const extendedImagingResultsSchema = z.object({
  ...DocumentDateSchema.shape,
  orderingProvider: practitionerSchema.nullable().describe(ORDERING_PROVIDER_PROMPT),
  companyName: z.string().nullable().describe(IMAGING_COMPANY_NAME_PROMPT),
  imagingType: z.nativeEnum(ImagingType).nullable(),
  imagingTypes: z.array(z.nativeEnum(ImagingType)).nullable(),
  bodyPart: z.string().nullable().describe(BODY_PART_PROMPT),
  bodyParts: z.array(z.string()).nullable().describe(BODY_PARTS_PROMPT),
  bodyPartLaterality: z.string().nullable().describe(BODY_PART_LATERALITY_PROMPT),
  needsSignature: z.boolean().nullable(),
  signatureRequired: z.boolean().nullable(),
  needsClarification: z.boolean().nullable(),
  procedure: z.string().nullable(),
  location: locationSchema.nullable(),
  significantFindings: z.boolean().nullable(),
});
const imagingResultsCustomFields: Record<ImagingResultsDocumentType, Record<string, z.ZodTypeAny>> = {
  [ImagingResultsDocumentType.IMAGING_RESULTS]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.BARIUM_SWALLOW]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.CLINICAL]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    bodyPart: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.COLONOSCOPY]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.CT]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    bodyPart: true,
    bodyPartLaterality: true,
    orderingProvider: true,
    procedure: true,
    signatureRequired: true,
  }).shape,
  [ImagingResultsDocumentType.DEXA]: extendedImagingResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [ImagingResultsDocumentType.ECHO]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.EEG]: extendedImagingResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [ImagingResultsDocumentType.EMG]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
    bodyPart: true,
    bodyPartLaterality: true,
  }).shape,
  [ImagingResultsDocumentType.ENG]: extendedImagingResultsSchema.pick({ dateOfEvent: true, orderingProvider: true })
    .shape,
  [ImagingResultsDocumentType.EKG]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
    companyName: true,
  }).shape,
  [ImagingResultsDocumentType.ESOPHAGRAM]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.HOLTER_MONITOR]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.MAMMOGRAM]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.MULTI]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.MRI]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
    bodyPart: true,
    bodyPartLaterality: true,
    signatureRequired: true,
  }).shape,
  [ImagingResultsDocumentType.NM]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.OTHER]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.OTHER_CARDIAC]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    procedure: true,
  }).shape,
  [ImagingResultsDocumentType.PET]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    bodyPart: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.PULMONARY_FUNCTION]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.RETINAL]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.STRESS_TEST]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.STRESS_TEST_EXERCISE]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.STRESS_TEST_NM]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    orderingProvider: true,
  }).shape,
  [ImagingResultsDocumentType.ULTRASOUND]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    bodyPart: true,
    bodyPartLaterality: true,
    companyName: true,
    orderingProvider: true,
    signatureRequired: true,
  }).shape,
  [ImagingResultsDocumentType.X_RAY]: extendedImagingResultsSchema.pick({
    dateOfEvent: true,
    bodyPart: true,
    companyName: true,
    orderingProvider: true,
    significantFindings: true,
  }).shape,
  [ImagingResultsDocumentType.NEEDS_CLARIFICATION]: extendedImagingResultsSchema.pick({
    dateReceived: true,
    bodyPart: true,
    imagingType: true,
  }).shape,
  [ImagingResultsDocumentType.NEEDS_SIGNATURE]: extendedImagingResultsSchema.pick({
    dateReceived: true,
    bodyPart: true,
    imagingType: true,
  }).shape,
  [ImagingResultsDocumentType.ENDOSCOPY]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.MONITOR_NOTIFICATION]: extendedImagingResultsSchema.shape,
  [ImagingResultsDocumentType.CARDIAC_LEG_IMAGING]: extendedImagingResultsSchema.shape,
};

const makeImagingResultsSchema = (type: ImagingResultsDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...baseImagingResultsFields,
    ...customFields,
  });

export const imagingResultsSchemas: Record<ImagingResultsDocumentType, z.ZodObject<any>> = (
  Object.values(ImagingResultsDocumentType) as ImagingResultsDocumentType[]
).reduce(
  (acc, type) => {
    acc[type] = makeImagingResultsSchema(type, imagingResultsCustomFields[type]);

    return acc;
  },
  {} as Record<ImagingResultsDocumentType, z.ZodObject<any>>
);

const imagingResultsSchemasArray = (Object.values(ImagingResultsDocumentType) as ImagingResultsDocumentType[]).map(
  (type) => imagingResultsSchemas[type]
) as [z.ZodObject<any>, ...z.ZodObject<any>[]];

export const anyImagingResultsSchema = z.discriminatedUnion("type", imagingResultsSchemasArray);

type ImagingResultsPersistedFields = {
  patient: Patient | null;
};

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type ImagingResults = Omit<
  ExtractedDocumentFields<
    ImagingResultsDocumentType,
    typeof extendedImagingResultsSchema,
    typeof baseImagingResultsFields
  >,
  keyof ImagingResultsPersistedFields
> &
  ImagingResultsPersistedFields &
  DatabaseDefaults;

const now = new Date().toISOString();

export const imagingResultsShape: ImagingResults = {
  id: "",
  type: ImagingResultsDocumentType.OTHER,
  patient: null,
  dateOfImagingResults: null,
  receivingLocation: null,
  receivingProvider: null,
  orderingProvider: null,
  companyName: null,
  bodyPart: null,
  bodyParts: null,
  bodyPartLaterality: null,
  imagingType: null,
  imagingTypes: null,
  studyType: null,
  procedure: null,
  abnormal: null,
  followUpRequired: null,
  impression: null,
  dateOfEvent: null,
  dateReceived: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
